import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";

export default function Questionnaire() {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [q, b] = await Promise.all([
          api.get("/config/questionnaire"),
          api.get(`/books/${bookId}`),
        ]);
        setQuestions(q.data.questions);
        setAnswers(b.data.questionnaire || {});
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, [bookId, navigate]);

  const current = questions[step];
  const total = questions.length;
  const progress = total ? Math.round((step / total) * 100) : 0;

  const setValue = (val) => setAnswers((a) => ({ ...a, [current.id]: val }));

  const persist = async (completed) => {
    await api.put(`/books/${bookId}/questionnaire`, { answers, completed });
  };

  const next = async () => {
    if (step < total - 1) {
      setDir(1);
      setStep(step + 1);
      persist(false).catch(() => {});
    } else {
      setSaving(true);
      try {
        await persist(true);
        toast.success("Questionario completato! Il team riceverà le tue risposte.");
        navigate(`/books/${bookId}`);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
      } finally {
        setSaving(false);
      }
    }
  };

  const back = () => {
    if (step > 0) {
      setDir(-1);
      setStep(step - 1);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/10 border-t-[#D4AF37]" />
      </div>
    );
  }

  const value = answers[current?.id] || "";
  const canProceed = current?.optional || value.toString().trim().length > 0;

  return (
    <div className="flex min-h-screen flex-col bg-ink">
      {/* Top bar + progress */}
      <div className="border-b border-white/5">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-5">
          <Logo />
          <button
            data-testid="questionnaire-exit-btn"
            onClick={() => navigate(`/books/${bookId}`)}
            className="flex items-center gap-2 text-sm text-white/40 transition-colors hover:text-white"
          >
            Salva ed esci <X className="h-4 w-4" />
          </button>
        </div>
        <Progress value={progress} className="h-1 rounded-none bg-white/5" data-testid="questionnaire-progress" />
      </div>

      {/* Question */}
      <div className="flex flex-1 items-center px-6">
        <div className="mx-auto w-full max-w-2xl">
          <p className="mb-6 font-mono text-sm text-[#D4AF37]">
            {String(step + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </p>
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={step}
              custom={dir}
              initial={{ opacity: 0, x: dir * 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: dir * -40 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            >
              {current.section && (
                <p className="mb-3 text-xs uppercase tracking-[0.3em] text-white/40" data-testid="questionnaire-section">
                  {current.section}
                </p>
              )}
              <h1 className="font-serif text-4xl font-light leading-tight text-white sm:text-5xl">
                {current.question}
                {current.optional && <span className="ml-2 align-middle text-sm text-white/30">(facoltativo)</span>}
              </h1>
              {current.hint && <p className="mt-4 text-white/40">{current.hint}</p>}

              <div className="mt-10">
                {current.type === "text" && (
                  <Input
                    data-testid="questionnaire-text-input"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder={current.placeholder}
                    autoFocus
                    className="h-14 border-0 border-b border-white/10 bg-transparent px-0 text-2xl text-white placeholder:text-white/20 focus-visible:ring-0"
                  />
                )}
                {current.type === "textarea" && (
                  <Textarea
                    data-testid="questionnaire-textarea-input"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder={current.placeholder}
                    autoFocus
                    rows={4}
                    className="border-white/10 bg-white/5 text-lg text-white placeholder:text-white/20"
                  />
                )}
                {current.type === "select" && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {current.options.map((opt) => {
                      const selected = value === opt;
                      return (
                        <button
                          key={opt}
                          data-testid={`questionnaire-option-${opt}`}
                          onClick={() => setValue(opt)}
                          className={`flex items-center justify-between rounded-xl border px-5 py-4 text-left text-white transition-all duration-200 ${
                            selected
                              ? "border-[#D4AF37] bg-[#D4AF37]/10"
                              : "border-white/10 bg-white/5 hover:border-white/20"
                          }`}
                        >
                          {opt}
                          {selected && <Check className="h-4 w-4 text-[#D4AF37]" />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="mt-12 flex items-center justify-between">
                <Button
                  variant="ghost"
                  onClick={back}
                  disabled={step === 0}
                  data-testid="questionnaire-back-btn"
                  className="gap-2 text-white/50 hover:bg-white/5 hover:text-white disabled:opacity-30"
                >
                  <ArrowLeft className="h-4 w-4" /> Indietro
                </Button>
                <Button
                  onClick={next}
                  disabled={!canProceed || saving}
                  data-testid="questionnaire-next-btn"
                  className="group h-12 gap-2 rounded-full bg-[#D4AF37] px-7 text-[#050A15] hover:bg-[#E5C158] disabled:opacity-40"
                >
                  {step === total - 1 ? (saving ? "Salvataggio…" : "Completa") : "Avanti"}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
